import React, { forwardRef, ReactNode, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { Button } from "../misc/Button";
import { Textarea } from "../misc/Textarea";
import { cn } from "../../utils/classMerger";

const MAX_CHARS = 1000;
const COUNT_FROM = 900;
const MAX_ROWS = 4;

interface NichePinnedBarProps {
    className?: string;
    children: ReactNode;
}

// The interview's pinned bottom block: the message box and what explains a locked one, on its own
// when there's no box (the write-up failure). It's the card's last block, below the conversation's
// scroll region, so it stays in view without sticking to the screen.
export const NichePinnedBar: React.FC<NichePinnedBarProps> = ({ className, children }) => (
    <div data-testid="niche-pinned" className={cn("border-t border-gray-200 bg-white px-3 py-2.5 rounded-b-lg", className)}>
        {children}
    </div>
);

interface NicheComposerProps {
    value: string;
    onChange: (text: string) => void;
    onSend: () => void;
    // Locked: a request is in flight, or the page is stale.
    disabled: boolean;
    // Send alone is off (a paused allowance); empty text turns it off too.
    sendDisabled: boolean;
    // What Send's disabled state is explained by (the paused line's id).
    describedBy?: string;
    // Above the message row: why the box or Send is locked (the paused line, a refusal, a failed reload).
    note?: ReactNode;
}

const px = (v: string): number => parseFloat(v) || 0;

// The interview's message box, pinned to the bottom of the card like a chat app's.
// Enter sends; Shift+Enter starts a new line.
const NicheComposer = forwardRef<HTMLTextAreaElement, NicheComposerProps>(
    ({ value, onChange, onSend, disabled, sendDisabled, describedBy, note }, ref) => {
        const box = useRef<HTMLTextAreaElement>(null);
        useImperativeHandle(ref, () => box.current as HTMLTextAreaElement);
        const canSend = !disabled && !sendDisabled && value.trim() !== "";

        // Two rows to start (the tip wraps fully on a phone), growing with the text up to four, then it
        // scrolls; back to two rows when emptied (a send, a new question). jsdom measures nothing
        // (scrollHeight 0), so it's left alone there.
        useLayoutEffect(() => {
            const el = box.current;
            if (!el) return;
            el.style.height = "";
            if (!value || !el.scrollHeight) return;
            const cs = window.getComputedStyle(el);
            const border = px(cs.borderTopWidth) + px(cs.borderBottomWidth);
            const max = (px(cs.lineHeight) || 24) * MAX_ROWS + px(cs.paddingTop) + px(cs.paddingBottom) + border;
            el.style.height = `${Math.min(el.scrollHeight + border, max)}px`;
        }, [value]);

        return (
            <NichePinnedBar>
                {note}
                <div className="flex items-end gap-2">
                    <Textarea
                        ref={box}
                        aria-label="Your answer"
                        rows={2}
                        maxLength={MAX_CHARS}
                        placeholder="A sentence or two. Towns and property types help most."
                        enterKeyHint="send"
                        // Never below two rows (two 24px lines, py-2 and a 1px border), whatever the auto-grow writes.
                        className="min-h-[66px] resize-none"
                        value={value}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
                            e.preventDefault();
                            if (canSend) onSend();
                        }}
                    />
                    <Button type="button" disabled={!canSend} aria-describedby={describedBy} onClick={onSend}>
                        Send
                    </Button>
                </div>
                {value.length >= COUNT_FROM && (
                    <p className="pt-1 text-right text-xs text-gray-500">{value.length.toLocaleString("en-US")} / 1,000</p>
                )}
            </NichePinnedBar>
        );
    },
);
NicheComposer.displayName = "NicheComposer";

export default NicheComposer;
